import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardHeader } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import type { IconComponent } from '@/components/atoms/SvgIcon/SvgIcon';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import { ModalBody, ModalComponent } from '@/components/molecules/ModalComponent';
import bioscope2 from '@/svg/bioscope-2-0.webp';
import bioscope1 from '@/svg/bioscope1-0.webp';
import winch from '@/svg/winch.webp';

import './Help.scss';
import styles from './HelpPage.module.scss';

interface HelpPageVideosProps {
    linkListBioscope1: {
        title: string;
        link: string;
    }[];
    linkListBioscope2: {
        title: string;
        link: string;
    }[];
    linkListWinch: {
        title: string;
        link: string;
    }[];
    headerText: string;
    headerIcon?: IconComponent;
    description: string;
}

function HelpPageVideos({
    linkListBioscope1,
    linkListBioscope2,
    linkListWinch,
    headerText,
    headerIcon,
    description,
}: HelpPageVideosProps) {
    const { t } = useTranslation();
    const [isModalOpen, setModalOpen] = useState(false);
    const [videoSrc, setVideoSrc] = useState<string | null>(null);

    const openModal = (e: React.MouseEvent, videoUrl: string) => {
        e.preventDefault();
        setVideoSrc(videoUrl);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setVideoSrc(null);
    };

    return (
        <div data-type="helpVideoSection">
            <CardHeader
                title={headerText}
                level="h2"
                size="card"
                icon={headerIcon && <TitleIcon icon={headerIcon} />}
            />
            <p>{description}</p>

            <div className={styles.videoColumns} data-type="helpPageVideoSection">
                <div>
                    <Caption as="h3">{t('help.pageVideos.bioscope10')}</Caption>
                    <div className="helpVideoContent">
                        <img src={bioscope1} alt="" />
                        <ul>
                            {linkListBioscope1.map((list, index) => {
                                return (
                                    <li key={index}>
                                        <a href="#" onClick={(e) => openModal(e, list.link)}>
                                            {list.title}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
                <div>
                    <Caption as="h3">{t('help.pageVideos.bioscope20')}</Caption>
                    <div className="helpVideoContent">
                        <img src={bioscope2} alt="" />
                        <ul>
                            {linkListBioscope2.map((list, index) => {
                                return (
                                    <li key={index}>
                                        <a href="#" onClick={(e) => openModal(e, list.link)}>
                                            {list.title}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
                <div>
                    <Caption as="h3">{t('help.pageVideos.winch')}</Caption>
                    <div className="helpVideoContent">
                        <img src={winch} alt="" data-video="winch" />
                        <ul>
                            {linkListWinch.map((list, index) => {
                                return (
                                    <li key={index}>
                                        <a href="#" onClick={(e) => openModal(e, list.link)}>
                                            {list.title}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
            <ModalComponent
                isOpen={isModalOpen}
                onClose={closeModal}
                size="lg"
                centered={false}
                title={t('help.pageVideos.installationVideo')}>
                <ModalBody>
                    {videoSrc ? (
                        <video width="100%" controls autoPlay>
                            <source src={videoSrc} type="video/mp4" />
                            <track default kind="captions" srcLang="en" />
                            {t('help.pageVideos.yourBrowserDoesNotSupport')}
                        </video>
                    ) : null}
                </ModalBody>
            </ModalComponent>
        </div>
    );
}
export default HelpPageVideos;
